import { Injectable, inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthService } from '@rockwell-automation-inc/service';
import { Observable, map, shareReplay } from 'rxjs';
import { LoggerService } from './logger.service';

export type Operator = 'all' | 'any';
export enum AdminPortalRole {
  RockwellAdmin = 'RockwellAdmin',
  CustomerSupport = 'CustomerSupport',
  Sales = 'RockwellSales',
  ServiceAdmin = 'SvcAdmin',
  TrialsManager = 'TrialsManager',
  ReadOnly = 'ReadOnly',
  DevSupport = 'DevSupport',
}

export enum Permission {
  CreateServiceRequest = 'create:servicerequest',
  CreateTrialCampaign = 'create:trialcampaign',
  ListEntitlement = 'list:entitlement',
  ListTenant = 'list:tenant',
  ListTrialCampaign = 'list:trialcampaign',
  ManageTrialCampaign = 'manage:trialcampaign',
  ListTrial = 'list:trial',
  ManageTrial = 'manage:trial',
  ListUser = 'list:user',
  ReadTenant = 'read:tenant',
  ReadUserRole = 'read:userrole',
  WriteTenant = 'write:tenant',
  WriteUser = 'write:user',
  //fake - for testing
  FakePerm = 'fakefakefake',
}

export type PermissionCondition = {
  operator?: Operator;
  permissions: Permission[];
};
export type AdminRoleCondition = {
  operator?: Operator;
  roles: AdminPortalRole[];
};

@Injectable({
  providedIn: 'root',
})
export class PermHelperService {
  private readonly authService = inject(AuthService);

  logger = inject(LoggerService).withContext(PermHelperService);
  helper = new JwtHelperService();
  permissionClaims$: Observable<Set<string>> = this.authService.getAccessToken$(false).pipe(
    map((token) => {
      const claims = this.helper.decodeToken(token);
      const permissions = new Set<string>(claims.permissions);
      claims.scope.split(/\s+/).forEach((s: any) => {
        permissions.add(s);
      });
      return permissions;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  checkImpl$(requestedPerms: string[], op: Operator = 'any'): Observable<boolean> {
    let method = requestedPerms.some;
    if (op === 'all') {
      method = requestedPerms.every;
    }
    return this.permissionClaims$.pipe(
      map((perms) => {
        const result = method.call(requestedPerms, (p) => perms.has(p));
        this.logger.debug(op, result, requestedPerms);
        return result;
      }),
    );
  }
  private roleToScopeGrant(role: AdminPortalRole): string {
    return `role:${role}`;
  }
  hasAll$ = (...requestedPerms: Permission[]): Observable<boolean> => this.checkImpl$(requestedPerms, 'all');
  hasAny$ = (...requestedPerms: Permission[]): Observable<boolean> => this.checkImpl$(requestedPerms, 'any');
  // Uncomment if needed in future
  // hasAllRoles$ = (...roles: AdminPortalRole[]): Observable<boolean> => this.checkImpl$(roles.map(this.roleToScopeGrant), 'all');
  // hasAnyRole$ = (...roles: AdminPortalRole[]): Observable<boolean> => this.checkImpl$(roles.map(this.roleToScopeGrant), 'any');
  hasRole$ = (role: AdminPortalRole): Observable<boolean> => this.checkImpl$([role].map(this.roleToScopeGrant), 'any');
  checkPermission$(c: PermissionCondition): Observable<boolean> {
    return this.checkImpl$(c.permissions, c.operator);
  }
  checkRoles$(c: AdminRoleCondition): Observable<boolean> {
    return this.checkImpl$(c.roles.map(this.roleToScopeGrant), c.operator);
  }
}
